/**
 * LibraryController - Manages the library panel
 * 
 * Handles template library UI: filtering, tabs (local/favorites),
 * drag & drop to canvas, multi-select, template CRUD operations.
 * Subscribes to events to keep library in sync with patch changes.
 */

import { generateId, escapeHtml } from '../utils/index.js';
import { deepClone } from '../utils/helpers.js';
import { eventBus, EVENTS } from '../services/EventBus.js';
import { showToast } from './ToastController.js';
import { showConfirmModal } from './ModalController.js';
import {
    getLibraryMultiSelect,
    clearLibraryMultiSelect
} from './SelectionController.js';

// ============= MODULE STATE =============
let libraryCollapsed = true;
let librarySearchFilter = '';
let libraryTab = 'local';
let libraryDragState = null;

// Dependencies (injected via init)
let definitionModel = null;
let instanceModel = null;
let connectionModel = null;
let favoritesService = null;
let globalDevicesService = null;
let state = null;  // For selectedTemplateId access

// Global library state
let globalSearchTimeout = null;
let globalSearchResults = [];
let globalSearchLoading = false;

// Global tab state
let globalManufacturers = [];
let globalManufacturerFilter = '';
let globalDeviceFilter = '';
let manufacturerDropdownVisible = false;
let scheduleAutosaveFn = null;
let recordActionFn = null;
let renderDeviceFn = null;
let clearDeviceSelectionFn = null;
let updateSelectionButtonsFn = null;
let renderAllCablesFn = null;
let updateConnectedPortsFn = null;
let screenToCanvasFn = null;
let createInstanceFromTemplateFn = null;
let getPointerPosFn = null;
let showEditTemplateModalFn = null;
let updateFooterLibraryStateFn = null;

// Reference to libraryMultiSelect from SelectionController
let libraryMultiSelect = null;

/**
 * Initialize the LibraryController
 * @param {Object} deps - Dependencies
 * @returns {Object} Public methods for onclick handlers
 */
export function initLibraryController(deps) {
    // Store dependencies
    definitionModel = deps.definitionModel;
    instanceModel = deps.instanceModel;
    connectionModel = deps.connectionModel;
    favoritesService = deps.favoritesService;
    globalDevicesService = deps.globalDevicesService;
    state = deps.state;
    scheduleAutosaveFn = deps.scheduleAutosave;
    recordActionFn = deps.recordAction;
    renderDeviceFn = deps.renderDevice;
    clearDeviceSelectionFn = deps.clearDeviceSelection;
    updateSelectionButtonsFn = deps.updateSelectionButtons;
    renderAllCablesFn = deps.renderAllCables;
    updateConnectedPortsFn = deps.updateConnectedPorts;
    screenToCanvasFn = deps.screenToCanvas;
    createInstanceFromTemplateFn = deps.createInstanceFromTemplate;
    getPointerPosFn = deps.getPointerPos;
    showEditTemplateModalFn = deps.showEditTemplateModal;
    updateFooterLibraryStateFn = deps.updateFooterLibraryState;
    
    // Get reference to library multi-select from SelectionController
    libraryMultiSelect = getLibraryMultiSelect();
    
    // Subscribe to events that should refresh library
    eventBus.on(EVENTS.DEVICE_ADDED, () => renderLibrary());
    eventBus.on(EVENTS.DEVICE_DELETED, () => renderLibrary());
    eventBus.on(EVENTS.PATCH_LOADED, () => renderLibrary());
    eventBus.on(EVENTS.AUTH_CHANGED, () => renderLibrary());
    
    // Set up drag & drop handlers on canvas container
    setupCanvasDragDrop();
    
    // Return public methods for window/onclick exposure
    return {
        setInitialLibraryState,
        toggleLibrary,
        filterLibrary,
        renderLibrary,
        handleLibraryItemClick,
        handleFavoriteItemClick,
        handleGlobalItemClick,
        handleLibraryDragStart,
        handleLibraryDragEnd,
        setLibraryTab,
        selectTemplate,
        editTemplate,
        editSelectedTemplate,
        duplicateSelectedTemplate,
        duplicateTemplate,
        deleteTemplate,
        deleteSelectedTemplate,
        addGlobalDeviceToFavorites,
        addGlobalDeviceToPatch,
        getLibraryState,
        getLibraryDragState,
        // Global tab methods
        handleManufacturerInput,
        showManufacturerDropdown,
        hideManufacturerDropdownDelayed,
        selectManufacturer,
        handleGlobalDeviceSearch,
        loadGlobalDefaults
    };
}

/**
 * Get current library state (for debugging/testing)
 */
function getLibraryState() {
    return {
        collapsed: libraryCollapsed,
        searchFilter: librarySearchFilter,
        tab: libraryTab
    };
}

/**
 * Get library drag state
 */
function getLibraryDragState() {
    return libraryDragState;
}

// ============= LIBRARY PANEL UI =============

/**
 * Set library default state based on screen size
 * Large screens (desktop, iPad): open by default
 * Small screens (iPhone, mobile): closed by default
 */
function setInitialLibraryState() {
    const BREAKPOINT = 768; // iPad portrait and larger = open
    const isLargeScreen = window.innerWidth >= BREAKPOINT;
    
    const panel = document.getElementById('library-panel');
    const container = document.querySelector('.canvas-container');
    const toggleBtn = document.getElementById('library-toggle-btn');
    
    if (isLargeScreen) {
        // Open library on large screens
        libraryCollapsed = false;
        panel.classList.remove('collapsed');
        container.classList.remove('library-collapsed');
        if (toggleBtn) toggleBtn.classList.add('active');
        if (updateFooterLibraryStateFn) updateFooterLibraryStateFn(true);
    } else {
        // Close library on small screens
        libraryCollapsed = true;
        panel.classList.add('collapsed');
        container.classList.add('library-collapsed');
        if (toggleBtn) toggleBtn.classList.remove('active');
        if (updateFooterLibraryStateFn) updateFooterLibraryStateFn(false);
    }
}

/**
 * Toggle library panel open/closed
 */
function toggleLibrary() {
    libraryCollapsed = !libraryCollapsed;
    const panel = document.getElementById('library-panel');
    const container = document.querySelector('.canvas-container');
    const toggleBtn = document.getElementById('library-toggle-btn');
    
    if (libraryCollapsed) {
        panel.classList.add('collapsed');
        container.classList.add('library-collapsed');
        if (toggleBtn) toggleBtn.classList.remove('active');
        if (updateFooterLibraryStateFn) updateFooterLibraryStateFn(false);
    } else {
        panel.classList.remove('collapsed');
        container.classList.remove('library-collapsed');
        if (toggleBtn) toggleBtn.classList.add('active');
        if (updateFooterLibraryStateFn) updateFooterLibraryStateFn(true);
    }
}

/**
 * Filter library by search query
 * @param {string} query - Search query
 */
function filterLibrary(query) {
    librarySearchFilter = query.toLowerCase();
    
    // For global tab, debounce the search since it hits the API
    if (libraryTab === 'global') {
        if (globalSearchTimeout) clearTimeout(globalSearchTimeout);
        globalSearchTimeout = setTimeout(() => {
            searchGlobalDevices(librarySearchFilter);
        }, 300);
    } else {
        renderLibrary();
    }
}

/**
 * Search global devices from Supabase
 * @param {string} query - Search query
 */
async function searchGlobalDevices(query) {
    if (!globalDevicesService) {
        console.warn('[Library] No globalDevicesService available');
        return;
    }
    
    globalSearchLoading = true;
    renderLibrary(); // Show loading state
    
    try {
        globalSearchResults = await globalDevicesService.search(query, 50);
    } catch (e) {
        console.error('[Library] Global search error:', e);
        globalSearchResults = [];
    }
    
    globalSearchLoading = false;
    renderLibrary();
}

/**
 * Set the active library tab
 * @param {string} tab - 'local', 'favorites', or 'global'
 */
function setLibraryTab(tab) {
    libraryTab = tab;
    
    // Clear multi-select when switching tabs
    libraryMultiSelect.selectedItems.clear();
    libraryMultiSelect.lastClickedId = null;
    
    // Update tab button states
    const localBtn = document.getElementById('filter-local-btn');
    const favBtn = document.getElementById('filter-favorites-btn');
    const globalBtn = document.getElementById('filter-global-btn');
    
    localBtn?.classList.remove('active');
    favBtn?.classList.remove('active');
    globalBtn?.classList.remove('active');
    
    // Toggle search field visibility
    const simpleSearch = document.getElementById('library-search-simple');
    const globalSearch = document.getElementById('library-search-global');
    const addSection = document.getElementById('library-add-section');
    const actionsSection = document.getElementById('library-actions-local');
    
    if (tab === 'global') {
        globalBtn?.classList.add('active');
        // Show global search fields, hide simple search
        if (simpleSearch) simpleSearch.style.display = 'none';
        if (globalSearch) globalSearch.style.display = 'flex';
        if (addSection) addSection.style.display = 'none';
        if (actionsSection) actionsSection.style.display = 'none';
        
        // Load manufacturers and show default devices
        loadGlobalDefaults();
        return;
    } else if (tab === 'favorites') {
        favBtn?.classList.add('active');
    } else {
        localBtn?.classList.add('active');
    }
    
    // Show simple search, hide global search for non-global tabs
    if (simpleSearch) simpleSearch.style.display = 'block';
    if (globalSearch) globalSearch.style.display = 'none';
    if (addSection) addSection.style.display = 'block';
    if (actionsSection) actionsSection.style.display = 'flex';
    
    renderLibrary();
}

/**
 * Load default global devices and manufacturer list
 */
async function loadGlobalDefaults() {
    if (!globalDevicesService) {
        renderLibrary();
        return;
    }
    
    globalSearchLoading = true;
    renderLibrary();
    
    try {
        // Load manufacturers in parallel with default devices
        const [manufacturers, defaultDevices] = await Promise.all([
            globalDevicesService.getManufacturers(),
            globalDevicesService.getDefault(20)
        ]);
        
        globalManufacturers = manufacturers;
        globalSearchResults = defaultDevices;
    } catch (e) {
        console.error('[Library] loadGlobalDefaults error:', e);
        globalManufacturers = [];
        globalSearchResults = [];
    }
    
    globalSearchLoading = false;
    renderLibrary();
}

/**
 * Handle manufacturer input changes
 * @param {string} value - Input value
 */
function handleManufacturerInput(value) {
    globalManufacturerFilter = value;
    
    // Update dropdown with filtered manufacturers
    updateManufacturerDropdown(value);
    
    // Debounce search
    if (globalSearchTimeout) clearTimeout(globalSearchTimeout);
    globalSearchTimeout = setTimeout(() => {
        searchGlobalAdvanced();
    }, 300);
}

/**
 * Update the manufacturer dropdown with filtered options
 * @param {string} filter - Filter string
 */
function updateManufacturerDropdown(filter) {
    const dropdown = document.getElementById('manufacturer-dropdown');
    if (!dropdown) return;
    
    const filterLower = (filter || '').toLowerCase();
    const filtered = globalManufacturers.filter(m => 
        m.toLowerCase().includes(filterLower)
    );
    
    if (filtered.length === 0) {
        dropdown.innerHTML = '<div class="manufacturer-dropdown-empty">No manufacturers found</div>';
    } else {
        dropdown.innerHTML = filtered.slice(0, 20).map(m => `
            <div class="manufacturer-dropdown-item ${m.toLowerCase() === filterLower ? 'selected' : ''}"
                 onmousedown="selectManufacturer('${escapeHtml(m)}')">${escapeHtml(m)}</div>
        `).join('');
    }
}

/**
 * Show the manufacturer dropdown
 */
function showManufacturerDropdown() {
    const dropdown = document.getElementById('manufacturer-dropdown');
    if (dropdown) {
        manufacturerDropdownVisible = true;
        dropdown.classList.add('open');
        updateManufacturerDropdown(globalManufacturerFilter);
    }
}

/**
 * Hide the manufacturer dropdown (with delay for click handling)
 */
function hideManufacturerDropdownDelayed() {
    setTimeout(() => {
        const dropdown = document.getElementById('manufacturer-dropdown');
        if (dropdown) {
            manufacturerDropdownVisible = false;
            dropdown.classList.remove('open');
        }
    }, 200);
}

/**
 * Select a manufacturer from dropdown
 * @param {string} manufacturer - Manufacturer name
 */
function selectManufacturer(manufacturer) {
    const input = document.getElementById('global-manufacturer-input');
    if (input) {
        input.value = manufacturer;
    }
    globalManufacturerFilter = manufacturer;
    
    const dropdown = document.getElementById('manufacturer-dropdown');
    if (dropdown) {
        dropdown.classList.remove('open');
    }
    
    // Trigger search immediately
    searchGlobalAdvanced();
}

/**
 * Handle device name search input
 * @param {string} value - Input value
 */
function handleGlobalDeviceSearch(value) {
    globalDeviceFilter = value;
    
    // Debounce search
    if (globalSearchTimeout) clearTimeout(globalSearchTimeout);
    globalSearchTimeout = setTimeout(() => {
        searchGlobalAdvanced();
    }, 300);
}

/**
 * Search global devices with advanced filters
 */
async function searchGlobalAdvanced() {
    if (!globalDevicesService) return;
    
    globalSearchLoading = true;
    renderLibrary();
    
    try {
        globalSearchResults = await globalDevicesService.searchAdvanced({
            manufacturer: globalManufacturerFilter,
            deviceName: globalDeviceFilter,
            limit: 50
        });
    } catch (e) {
        console.error('[Library] searchGlobalAdvanced error:', e);
        globalSearchResults = [];
    }
    
    globalSearchLoading = false;
    renderLibrary();
}

/**
 * Render the library panel contents
 */
function renderLibrary() {
    const list = document.getElementById('library-list');
    if (!list) return;
    
    // Handle global tab separately
    if (libraryTab === 'global') {
        renderGlobalLibrary(list);
        return;
    }
    
    // Get templates based on active tab
    let templates;
    if (libraryTab === 'favorites') {
        templates = favoritesService.getAllAsArray();
    } else {
        templates = definitionModel.getAll();
    }
    
    // Apply search filter
    let filtered = templates;
    if (librarySearchFilter) {
        filtered = filtered.filter(t => 
            t.name.toLowerCase().includes(librarySearchFilter) ||
            (t.manufacturer && t.manufacturer.toLowerCase().includes(librarySearchFilter))
        );
    }
    
    if (filtered.length === 0) {
        let emptyMsg;
        if (libraryTab === 'favorites') {
            if (favoritesService.getCount() === 0) {
                emptyMsg = 'No favorites yet.<br>Click ★ on a device to add it.';
            } else {
                emptyMsg = 'No matching favorites.';
            }
        } else if (librarySearchFilter) {
            emptyMsg = 'No matching devices.';
        } else {
            emptyMsg = 'No devices in this patch.<br>Click "+ Add Device" to create one.';
        }
        list.innerHTML = `<div class="library-empty">${emptyMsg}</div>`;
        return;
    }
    
    // Show multi-selection badge if multiple items selected (only in local tab)
    let multiBadgeHtml = '';
    if (libraryTab === 'local' && libraryMultiSelect.selectedItems.size > 1) {
        multiBadgeHtml = `<div class="library-multi-badge">📦 ${libraryMultiSelect.selectedItems.size} devices selected</div>`;
    }
    
    list.innerHTML = multiBadgeHtml + filtered.map(template => {
        const isFavorited = favoritesService.isFavorite(template.id);
        
        if (libraryTab === 'favorites') {
            // Favorites tab: click to select, drag to add to patch
            const isPrimary = state.selectedTemplateId === template.id;
            const isMultiSelected = libraryMultiSelect.selectedItems.has(template.id);
            
            const classes = ['library-item'];
            if (isPrimary) classes.push('selected');
            if (isMultiSelected) classes.push('multi-selected');
            
            return `
                <div class="${classes.join(' ')}" 
                     data-template-id="${template.id}"
                     data-source="favorites"
                     onclick="handleFavoriteItemClick(event, '${template.id}')"
                     ondblclick="editTemplate('${template.id}')"
                     draggable="true"
                     ondragstart="handleLibraryDragStart(event, '${template.id}', true)"
                     ondragend="handleLibraryDragEnd(event)">
                    <button class="library-item-favorite favorited" 
                            onclick="event.stopPropagation(); toggleFavorite('${template.id}')"
                            ontouchend="event.stopPropagation(); event.preventDefault(); toggleFavorite('${template.id}')"
                            title="Remove from favorites">
                        ★
                    </button>
                    <div class="library-item-info">
                        <div class="library-item-name">${escapeHtml(template.name)}</div>
                        <div class="library-item-meta">
                            ${template.manufacturer ? `<span class="library-item-manufacturer">${escapeHtml(template.manufacturer)}</span>` : ''}
                            ${template.version ? `<span class="library-item-version">${escapeHtml(template.version)}</span>` : ''}
                        </div>
                    </div>
                </div>
            `;
        } else {
            // Local tab: show instance count and favorite button
            const instanceCount = instanceModel.getByTemplateId(template.id).length;
            const isPrimary = state.selectedTemplateId === template.id;
            const isMultiSelected = libraryMultiSelect.selectedItems.has(template.id);
            
            const classes = ['library-item'];
            if (isPrimary) classes.push('selected');
            if (isMultiSelected) classes.push('multi-selected');
            
            return `
                <div class="${classes.join(' ')}" 
                     data-template-id="${template.id}"
                     onclick="handleLibraryItemClick(event, '${template.id}')"
                     ondblclick="editTemplate('${template.id}')"
                     draggable="true"
                     ondragstart="handleLibraryDragStart(event, '${template.id}')"
                     ondragend="handleLibraryDragEnd(event)">
                    <button class="library-item-favorite ${isFavorited ? 'favorited' : ''}" 
                            onclick="event.stopPropagation(); toggleFavorite('${template.id}')"
                            ontouchend="event.stopPropagation(); event.preventDefault(); toggleFavorite('${template.id}')"
                            title="${isFavorited ? 'Remove from favorites' : 'Add to favorites'}">
                        ★
                    </button>
                    <div class="library-item-info">
                        <div class="library-item-name">${escapeHtml(template.name)}</div>
                        <div class="library-item-meta">
                            ${template.manufacturer ? `<span class="library-item-manufacturer">${escapeHtml(template.manufacturer)}</span>` : ''}
                            ${template.version ? `<span class="library-item-version">${escapeHtml(template.version)}</span>` : ''}
                        </div>
                    </div>
                    <span class="library-item-count ${instanceCount === 0 ? 'zero' : ''}">${instanceCount}</span>
                </div>
            `;
        }
    }).join('');
    
    // Emit library updated event
    eventBus.emit(EVENTS.LIBRARY_UPDATED, { tab: libraryTab, count: filtered.length });
}

/**
 * Render the global library contents
 * @param {HTMLElement} list - The library list element
 */
function renderGlobalLibrary(list) {
    // Show loading state
    if (globalSearchLoading) {
        list.innerHTML = `<div class="library-loading">
            <div class="spinner"></div>
            <div>Searching global library...</div>
        </div>`;
        return;
    }
    
    // No service available
    if (!globalDevicesService) {
        list.innerHTML = `<div class="library-empty">
            🔒 Login required<br>
            <small>Sign in to browse the global device library.</small>
        </div>`;
        return;
    }
    
    // Empty results
    if (globalSearchResults.length === 0) {
        const hasFilters = globalManufacturerFilter || globalDeviceFilter;
        if (hasFilters) {
            list.innerHTML = `<div class="library-empty">
                No matching devices found.<br>
                <small>Try a different search term.</small>
            </div>`;
        } else {
            list.innerHTML = `<div class="library-empty">
                🌍 Global Device Library<br>
                <small>Loading devices...</small>
            </div>`;
            // Try loading defaults
            loadGlobalDefaults();
        }
        return;
    }
    
    // Render results
    list.innerHTML = globalSearchResults.map(device => {
        const isFavorited = favoritesService.isFavorite(device.id);
        const existsInPatch = definitionModel.exists(device.id);
        
        return `
            <div class="library-item library-item-global" 
                 data-template-id="${device.id}"
                 data-source="global"
                 onclick="handleGlobalItemClick(event, '${device.id}')"
                 draggable="true"
                 ondragstart="handleLibraryDragStart(event, '${device.id}', false, true)"
                 ondragend="handleLibraryDragEnd(event)">
                <button class="library-item-favorite ${isFavorited ? 'favorited' : ''}" 
                        onclick="event.stopPropagation(); addGlobalDeviceToFavorites('${device.id}')"
                        ontouchend="event.stopPropagation(); event.preventDefault(); addGlobalDeviceToFavorites('${device.id}')"
                        title="${isFavorited ? 'Already in favorites' : 'Add to favorites'}">
                    ★
                </button>
                <div class="library-item-info">
                    <div class="library-item-name">${escapeHtml(device.name)}</div>
                    <div class="library-item-meta">
                        ${device.manufacturer ? `<span class="library-item-manufacturer">${escapeHtml(device.manufacturer)}</span>` : ''}
                        ${device.version ? `<span class="library-item-version">${escapeHtml(device.version)}</span>` : ''}
                    </div>
                </div>
                ${existsInPatch ? '<span class="library-item-badge">✓</span>' : ''}
            </div>
        `;
    }).join('');
    
    // Emit library updated event
    eventBus.emit(EVENTS.LIBRARY_UPDATED, { tab: 'global', count: globalSearchResults.length });
}

// ============= LIBRARY ITEM INTERACTIONS =============

/**
 * Handle click on a library item (local tab)
 * Supports modifier keys for multi-selection
 * @param {Event} e - Click event
 * @param {string} templateId - Template ID
 */
function handleLibraryItemClick(e, templateId) {
    const isCtrl = e.ctrlKey || e.metaKey;
    const isShift = e.shiftKey;
    
    // Get filtered list for shift-click range selection
    const filteredTemplates = librarySearchFilter
        ? definitionModel.search(librarySearchFilter)
        : definitionModel.getAll();
    const filteredIds = filteredTemplates.map(t => t.id);
    
    if (isShift && libraryMultiSelect.lastClickedId) {
        // Shift+click: select range
        const lastIdx = filteredIds.indexOf(libraryMultiSelect.lastClickedId);
        const currIdx = filteredIds.indexOf(templateId);
        
        if (lastIdx !== -1 && currIdx !== -1) {
            const startIdx = Math.min(lastIdx, currIdx);
            const endIdx = Math.max(lastIdx, currIdx);
            
            // If Ctrl is NOT held, clear selection first
            if (!isCtrl) {
                libraryMultiSelect.selectedItems.clear();
            }
            
            // Add range to selection
            for (let i = startIdx; i <= endIdx; i++) {
                libraryMultiSelect.selectedItems.add(filteredIds[i]);
            }
            
            // Primary selection stays at first clicked item
            if (!state.selectedTemplateId || !libraryMultiSelect.selectedItems.has(state.selectedTemplateId)) {
                state.selectedTemplateId = libraryMultiSelect.lastClickedId;
            }
        }
    } else if (isCtrl) {
        // Ctrl+click: toggle item in selection
        if (libraryMultiSelect.selectedItems.has(templateId)) {
            libraryMultiSelect.selectedItems.delete(templateId);
            // If we removed the primary selection, pick another
            if (state.selectedTemplateId === templateId) {
                state.selectedTemplateId = libraryMultiSelect.selectedItems.size > 0 
                    ? libraryMultiSelect.selectedItems.values().next().value 
                    : null;
            }
        } else {
            libraryMultiSelect.selectedItems.add(templateId);
            // Make this the primary selection if none exists
            if (!state.selectedTemplateId) {
                state.selectedTemplateId = templateId;
            }
        }
        libraryMultiSelect.lastClickedId = templateId;
    } else {
        // Normal click: single selection
        libraryMultiSelect.selectedItems.clear();
        libraryMultiSelect.selectedItems.add(templateId);
        libraryMultiSelect.lastClickedId = templateId;
        state.selectedTemplateId = templateId;
    }
    
    // Deselect canvas items
    if (clearDeviceSelectionFn) clearDeviceSelectionFn();
    state.selectedConnectionId = null;
    
    if (updateSelectionButtonsFn) updateSelectionButtonsFn();
    renderLibrary();
}

/**
 * Handle click on favorites tab item
 * Select the template (drag & drop is used to add to patch)
 * @param {Event} e - Click event
 * @param {string} templateId - Template ID
 */
function handleFavoriteItemClick(e, templateId) {
    const template = favoritesService.get(templateId);
    if (!template) return;
    
    // Just select the template (like local tab behavior)
    // Clear multi-select and set single selection
    libraryMultiSelect.selectedItems.clear();
    libraryMultiSelect.selectedItems.add(templateId);
    libraryMultiSelect.lastClickedId = templateId;
    state.selectedTemplateId = templateId;
    
    // Deselect canvas items
    if (clearDeviceSelectionFn) clearDeviceSelectionFn();
    state.selectedConnectionId = null;
    
    if (updateSelectionButtonsFn) updateSelectionButtonsFn();
    renderLibrary();
}

/**
 * Handle click on global library item
 * Adds the device to the patch on click
 * @param {Event} e - Click event
 * @param {string} deviceId - Global device ID
 */
function handleGlobalItemClick(e, deviceId) {
    // Find the device in search results
    const device = globalSearchResults.find(d => d.id === deviceId);
    if (!device) return;
    
    // Add to patch
    addGlobalDeviceToPatch(deviceId);
}

/**
 * Add a global device to favorites
 * @param {string} deviceId - Global device ID
 */
function addGlobalDeviceToFavorites(deviceId) {
    const device = globalSearchResults.find(d => d.id === deviceId);
    if (!device) return;
    
    if (favoritesService.isFavorite(deviceId)) {
        showToast('Already in favorites');
        return;
    }
    
    // Create template from global device
    const template = {
        id: device.id,
        name: device.name,
        manufacturer: device.manufacturer,
        version: device.version,
        portGroups: device.portGroups || [],
        isRouter: device.isRouter || false
    };
    
    favoritesService.add(deviceId, template);
    showToast(`Added "${device.name}" to favorites`);
    renderLibrary();
}

/**
 * Add a global device to the current patch
 * @param {string} deviceId - Global device ID
 */
function addGlobalDeviceToPatch(deviceId) {
    const device = globalSearchResults.find(d => d.id === deviceId);
    if (!device) return;
    
    // Check if already exists in patch
    if (definitionModel.exists(deviceId)) {
        showToast('Device already in patch');
        return;
    }
    
    // Create template from global device
    const template = {
        id: device.id,
        name: device.name,
        manufacturer: device.manufacturer,
        version: device.version,
        portGroups: device.portGroups || [],
        isRouter: device.isRouter || false
    };
    
    // Add template to patch
    definitionModel.add(template);
    
    // Create an instance on the canvas
    if (createInstanceFromTemplateFn) {
        createInstanceFromTemplateFn(deviceId);
    }
    
    if (scheduleAutosaveFn) scheduleAutosaveFn();
    showToast(`Added "${device.name}" to patch`);
    renderLibrary();
}

/**
 * Select a template programmatically
 * @param {string} templateId - Template ID
 */
function selectTemplate(templateId) {
    handleLibraryItemClick({ ctrlKey: false, shiftKey: false, metaKey: false }, templateId);
}

/**
 * Edit a template (open edit modal)
 * @param {string} templateId - Template ID
 */
function editTemplate(templateId) {
    // Try local first, then favorites
    let template = definitionModel.getById(templateId);
    if (!template) {
        template = favoritesService.get(templateId);
    }
    if (template && showEditTemplateModalFn) {
        showEditTemplateModalFn(template);
    }
}

/**
 * Edit the first selected template
 */
function editSelectedTemplate() {
    // Get first selected template (from multi-select or single selection)
    const templateId = libraryMultiSelect.selectedItems.size > 0 
        ? libraryMultiSelect.selectedItems.values().next().value
        : state.selectedTemplateId;
    
    if (!templateId) {
        showToast('Select a device in the library first', true);
        return;
    }
    
    // Get template from local (definitionModel) or favorites (favoritesService)
    let template = definitionModel.getById(templateId);
    if (!template && libraryTab === 'favorites') {
        template = favoritesService.get(templateId);
    }
    
    if (template && showEditTemplateModalFn) {
        showEditTemplateModalFn(template);
    } else {
        showToast('Device not found', true);
    }
}

// ============= TEMPLATE CRUD OPERATIONS =============

/**
 * Duplicate a template
 * @param {string} templateId - Template ID to duplicate
 * @returns {string|null} New template ID or null if failed
 */
function duplicateTemplate(templateId) {
    const template = definitionModel.getById(templateId);
    if (!template) return null;
    
    const newTemplate = {
        id: generateId(),
        name: template.name + ' (Copy)',
        manufacturer: template.manufacturer,
        version: template.version,
        portGroups: template.portGroups.map(g => ({
            ...g,
            id: generateId()
        }))
    };
    
    // Preserve isRouter if present
    if ('isRouter' in template) {
        newTemplate.isRouter = !!template.isRouter;
    }
    
    definitionModel.add(newTemplate);
    if (scheduleAutosaveFn) scheduleAutosaveFn();
    return newTemplate.id;
}

/**
 * Duplicate all selected templates
 */
function duplicateSelectedTemplate() {
    // Get all selected template IDs (multi-select or single)
    const templateIds = libraryMultiSelect.selectedItems.size > 0 
        ? Array.from(libraryMultiSelect.selectedItems)
        : (state.selectedTemplateId ? [state.selectedTemplateId] : []);
    
    if (templateIds.length === 0) {
        showToast('Select a device in the library first', true);
        return;
    }
    
    // Duplicate all selected templates
    const newIds = [];
    templateIds.forEach(tid => {
        const newId = duplicateTemplate(tid);
        if (newId) newIds.push(newId);
    });
    
    // Select the newly created templates
    if (newIds.length > 0) {
        libraryMultiSelect.selectedItems.clear();
        newIds.forEach(id => libraryMultiSelect.selectedItems.add(id));
        state.selectedTemplateId = newIds[0];
        libraryMultiSelect.lastClickedId = newIds[0];
        renderLibrary();
        showToast(newIds.length > 1 ? `Duplicated ${newIds.length} templates` : 'Template duplicated');
    }
}

/**
 * Delete a template and all its instances
 * @param {string} templateId - Template ID to delete
 */
async function deleteTemplate(templateId) {
    const template = definitionModel.getById(templateId);
    if (!template) return;
    
    const instances = instanceModel.getByTemplateId(templateId);
    
    if (instances.length > 0) {
        const confirmed = await showConfirmModal({
            title: 'Delete Template',
            message: `This will delete ${instances.length} instance(s) on the canvas. Continue?`,
            confirmText: 'Delete',
            danger: true
        });
        if (!confirmed) return;
    }
    
    // Capture state for undo
    const deletedTemplate = deepClone(template);
    const deletedDevices = instances.map(d => deepClone(d));
    const deletedConnections = [];
    instances.forEach(d => {
        connectionModel.filter(c => 
            c.sourceDeviceId === d.id || c.targetDeviceId === d.id
        ).forEach(c => {
            if (!deletedConnections.some(dc => dc.id === c.id)) {
                deletedConnections.push(deepClone(c));
            }
        });
    });
    
    // Delete instances and their connections
    instances.forEach(d => {
        connectionModel.removeByDeviceId(d.id);
        document.getElementById(d.id)?.remove();
    });
    instanceModel.removeByTemplateId(templateId);
    
    // Delete the template
    definitionModel.remove(templateId);
    
    // Clear selection if this was selected
    if (state.selectedTemplateId === templateId) {
        state.selectedTemplateId = null;
    }
    libraryMultiSelect.selectedItems.delete(templateId);
    
    renderLibrary();
    if (renderAllCablesFn) renderAllCablesFn();
    if (scheduleAutosaveFn) scheduleAutosaveFn();
    
    // Record action for undo/redo
    if (recordActionFn) {
        recordActionFn(
            'delete-template',
            `Delete ${deletedTemplate.name}`,
            () => {
                definitionModel.add(deepClone(deletedTemplate));
                deletedDevices.forEach(device => {
                    instanceModel.add(deepClone(device));
                    if (renderDeviceFn) renderDeviceFn(device);
                });
                deletedConnections.forEach(conn => {
                    connectionModel.add(deepClone(conn));
                });
                if (renderAllCablesFn) renderAllCablesFn();
                if (updateConnectedPortsFn) updateConnectedPortsFn();
                renderLibrary();
                if (scheduleAutosaveFn) scheduleAutosaveFn();
            },
            () => {
                deletedDevices.forEach(device => {
                    connectionModel.removeByDeviceId(device.id);
                    instanceModel.remove(device.id);
                    document.getElementById(device.id)?.remove();
                });
                definitionModel.remove(templateId);
                if (renderAllCablesFn) renderAllCablesFn();
                if (updateConnectedPortsFn) updateConnectedPortsFn();
                renderLibrary();
                if (scheduleAutosaveFn) scheduleAutosaveFn();
            }
        );
    }
    
    showToast('Template deleted');
}

/**
 * Delete all selected templates
 */
async function deleteSelectedTemplate() {
    // Get all selected template IDs (multi-select or single)
    const templateIds = libraryMultiSelect.selectedItems.size > 0 
        ? Array.from(libraryMultiSelect.selectedItems)
        : (state.selectedTemplateId ? [state.selectedTemplateId] : []);
    
    if (templateIds.length === 0) {
        showToast('Select a device in the library first', true);
        return;
    }
    
    // Count total instances across all selected templates
    const totalInstances = templateIds.reduce((sum, tid) => 
        sum + instanceModel.getByTemplateId(tid).length, 0);
    
    if (totalInstances > 0) {
        const msg = templateIds.length > 1 
            ? `This will delete ${templateIds.length} templates and ${totalInstances} instance(s) on the canvas. Continue?`
            : `This will delete ${totalInstances} instance(s) on the canvas. Continue?`;
        const confirmed = await showConfirmModal({
            title: 'Delete Templates',
            message: msg,
            confirmText: 'Delete',
            danger: true
        });
        if (!confirmed) return;
    } else if (templateIds.length > 1) {
        const confirmed = await showConfirmModal({
            title: 'Delete Templates',
            message: `Delete ${templateIds.length} templates?`,
            confirmText: 'Delete',
            danger: true
        });
        if (!confirmed) return;
    }
    
    // Capture state before deletion for undo
    const deletedTemplates = templateIds.map(tid => {
        const t = definitionModel.getById(tid);
        return t ? deepClone(t) : null;
    }).filter(Boolean);
    
    const deletedDevices = [];
    const deletedConnections = [];
    
    templateIds.forEach(templateId => {
        instanceModel.getByTemplateId(templateId).forEach(d => {
            deletedDevices.push(deepClone(d));
            // Capture connections involving this device
            connectionModel.filter(c => 
                c.sourceDeviceId === d.id || c.targetDeviceId === d.id
            ).forEach(c => {
                if (!deletedConnections.some(dc => dc.id === c.id)) {
                    deletedConnections.push(deepClone(c));
                }
            });
        });
    });
    
    // Delete all instances for selected templates
    templateIds.forEach(templateId => {
        const toDelete = instanceModel.getByTemplateId(templateId);
        toDelete.forEach(d => {
            connectionModel.removeByDeviceId(d.id);
            document.getElementById(d.id)?.remove();
        });
        instanceModel.removeByTemplateId(templateId);
    });
    
    // Delete the templates
    definitionModel.removeMany(templateIds);
    
    // Clear selections
    clearLibraryMultiSelect();
    
    renderLibrary();
    if (renderAllCablesFn) renderAllCablesFn();
    if (scheduleAutosaveFn) scheduleAutosaveFn();
    
    // Record action for undo/redo
    const description = templateIds.length > 1 
        ? `Delete ${templateIds.length} templates` 
        : `Delete ${deletedTemplates[0]?.name || 'template'}`;
    
    if (recordActionFn) {
        recordActionFn(
            'delete-template',
            description,
            // Undo: restore templates, devices, and connections
            () => {
                // Restore templates first
                deletedTemplates.forEach(template => {
                    definitionModel.add(deepClone(template));
                });
                // Restore devices
                deletedDevices.forEach(device => {
                    instanceModel.add(deepClone(device));
                    if (renderDeviceFn) renderDeviceFn(device);
                });
                // Restore connections
                deletedConnections.forEach(conn => {
                    connectionModel.add(deepClone(conn));
                });
                if (renderAllCablesFn) renderAllCablesFn();
                if (updateConnectedPortsFn) updateConnectedPortsFn();
                renderLibrary();
                if (scheduleAutosaveFn) scheduleAutosaveFn();
            },
            // Redo: delete again
            () => {
                // Delete devices and their connections
                deletedDevices.forEach(device => {
                    connectionModel.removeByDeviceId(device.id);
                    instanceModel.remove(device.id);
                    document.getElementById(device.id)?.remove();
                });
                // Delete templates
                definitionModel.removeMany(deletedTemplates.map(t => t.id));
                clearLibraryMultiSelect();
                if (renderAllCablesFn) renderAllCablesFn();
                if (updateConnectedPortsFn) updateConnectedPortsFn();
                renderLibrary();
                if (scheduleAutosaveFn) scheduleAutosaveFn();
            }
        );
    }
    
    showToast(templateIds.length > 1 ? `Deleted ${templateIds.length} templates` : 'Template deleted');
}

// ============= LIBRARY DRAG & DROP =============

/**
 * Handle drag start from library item
 * @param {DragEvent} e - Drag event
 * @param {string} templateId - Template ID being dragged
 * @param {boolean} fromFavorites - Whether dragging from favorites tab
 * @param {boolean} fromGlobal - Whether dragging from global tab
 */
function handleLibraryDragStart(e, templateId, fromFavorites = false, fromGlobal = false) {
    libraryDragState = { templateId, fromFavorites, fromGlobal };
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', templateId);
    
    // Get template from appropriate source
    let template;
    if (fromGlobal) {
        template = globalSearchResults.find(d => d.id === templateId);
    } else if (fromFavorites) {
        template = favoritesService.get(templateId);
    } else {
        template = definitionModel.getById(templateId);
    }
    
    if (template) {
        // Create custom drag image
        const preview = document.createElement('div');
        preview.className = 'library-drag-preview';
        preview.textContent = template.name;
        document.body.appendChild(preview);
        e.dataTransfer.setDragImage(preview, 0, 0);
        setTimeout(() => preview.remove(), 0);
    }
    
    e.target.classList.add('dragging');
}

/**
 * Handle drag end from library item
 * @param {DragEvent} e - Drag event
 */
function handleLibraryDragEnd(e) {
    e.target.classList.remove('dragging');
    libraryDragState = null;
}

/**
 * Set up drag & drop handlers on canvas container
 */
function setupCanvasDragDrop() {
    const canvasContainer = document.querySelector('.canvas-container');
    if (!canvasContainer) return;
    
    canvasContainer.addEventListener('dragover', (e) => {
        if (libraryDragState) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
        }
    });

    canvasContainer.addEventListener('drop', (e) => {
        if (libraryDragState) {
            e.preventDefault();
            
            let templateId = libraryDragState.templateId;
            
            // If dragging from global, add to local first if not already there
            if (libraryDragState.fromGlobal) {
                const existsInLocal = definitionModel.exists(templateId);
                if (!existsInLocal) {
                    const globalDevice = globalSearchResults.find(d => d.id === templateId);
                    if (globalDevice) {
                        const template = {
                            id: globalDevice.id,
                            name: globalDevice.name,
                            manufacturer: globalDevice.manufacturer,
                            version: globalDevice.version,
                            portGroups: globalDevice.portGroups || [],
                            isRouter: globalDevice.isRouter || false
                        };
                        definitionModel.add(deepClone(template));
                    }
                }
            }
            // If dragging from favorites, add to local first if not already there
            else if (libraryDragState.fromFavorites) {
                const existsInLocal = definitionModel.exists(templateId);
                if (!existsInLocal) {
                    const favTemplate = favoritesService.get(templateId);
                    if (favTemplate) {
                        definitionModel.add(deepClone(favTemplate));
                    }
                }
            }
            
            const eventPos = getPointerPosFn(e);
            const pos = screenToCanvasFn(eventPos.clientX, eventPos.clientY);
            if (createInstanceFromTemplateFn) {
                createInstanceFromTemplateFn(templateId, pos);
            }
            libraryDragState = null;
            renderLibrary();
        }
    });
}
